import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * Shared photo-divider backdrop. Currently used by LeaderboardDividerSection
 * (was also used by the Control Center section, removed per GHS5-98). A reusable
 * component cycling the same image set with identical sizing + timing — each
 * mount runs its own timer from image 1.
 *
 * Sizing model (pure CSS — no JS measurement):
 *   - An invisible in-flow `sizer` (max-w-[1600px], aspect 1600/935) drives
 *     the section height; `min-h-[750px]` on the section floors it.
 *   - The sharp carousel is absolute, centered, capped at 1600px, h-full,
 *     object-cover. The three regimes fall out automatically:
 *       >1600px     → height locks at 935, image caps at 1600 centered →
 *                     exact fit, blurred copy fills the side gutters.
 *       ~1283–1600  → section grows with the image, full-bleed, exact fit.
 *       <1283px     → floored at 750px, image covers full width → sides
 *                     clip, no gutter so the blur layer is never visible.
 *   - The blur layer is the always-SM image, full-bleed + scaled, so it only
 *     ever shows in the >1600px gutter case (i.e. never on mobile).
 *
 * `isolate` keeps a stable stacking context so the negative-z photo layers
 * stay above <main>'s background gradient after Framer Motion strips the
 * entrance transform (see the original section comments for the full why).
 */

interface CarouselImage {
  /** Sharp source for ≥ md (up to the 1600px cap). */
  lg: string;
  /** Sharp source for small screens + the always-on blur layer. */
  sm: string;
}

const IMAGES: CarouselImage[] = [
  {
    lg: '/assets/images/carrousel/Image_1_LG.webp',
    sm: '/assets/images/carrousel/Image_1_SM.webp',
  },
  {
    lg: '/assets/images/carrousel/Image_2_LG.webp',
    sm: '/assets/images/carrousel/Image_2_SM.webp',
  },
];

const HOLD_MS = 6000; // dwell on each image
const FADE_MS = 900; // crossfade duration
const SM_BREAKPOINT_PX = 767; // ≤ this, the sharp layer uses the SM source

interface Props {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /**
   * Per-section tint, rendered above the photo and below the foreground.
   * Provide an absolutely-positioned `-z-10` element (inset-0).
   */
  overlay?: ReactNode;
  /** Foreground content, placed in a bottom-anchored, padded wrapper. */
  children: ReactNode;
}

export default function CarouselDivider({
  ariaLabel,
  ariaLabelledBy,
  overlay,
  children,
}: Props) {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  // Auto-advance. Disabled under reduced-motion (stays on image 1) and when
  // there's nothing to cycle.
  useEffect(() => {
    if (reduce || IMAGES.length < 2) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % IMAGES.length),
      HOLD_MS,
    );
    return () => clearInterval(id);
  }, [reduce]);

  const fade = reduce ? '0ms' : `${FADE_MS}ms`;

  return (
    <motion.section
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className="relative isolate min-h-[750px] overflow-hidden"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut' }}
    >
      {/* Blur fill — full-bleed, always SM, blurred + scaled so its edges
          push off-screen. Only visible in the >1600px gutter case. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-30 select-none"
      >
        {IMAGES.map((img, i) => (
          <img
            key={img.sm}
            src={img.sm}
            alt=""
            className={`absolute inset-0 h-full w-full scale-110 object-cover blur-2xl transition-opacity ease-out ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDuration: fade }}
          />
        ))}
      </div>

      {/* Sizer — invisible, in normal flow. Drives section height up to the
          1600px cap; min-h-[750px] on the section floors it on narrow
          screens. */}
      <div
        aria-hidden="true"
        className="pointer-events-none invisible mx-auto w-full max-w-[1600px]"
        style={{ aspectRatio: '1600 / 935' }}
      />

      {/* Sharp carousel — centered, capped at 1600px, h-full = section
          height. object-cover gives an exact fit above 1283px (no crop) and
          clips the sides when floored at 750px. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-1/2 -z-20 w-full max-w-[1600px] -translate-x-1/2 select-none"
      >
        {IMAGES.map((img, i) => (
          <picture
            key={img.lg}
            className={`absolute inset-0 transition-opacity ease-out ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDuration: fade }}
          >
            <source media={`(max-width: ${SM_BREAKPOINT_PX}px)`} srcSet={img.sm} />
            <img src={img.lg} alt="" className="h-full w-full object-cover" />
          </picture>
        ))}
      </div>

      {/* Per-section tint. */}
      {overlay}

      {/* Foreground — bottom-anchored, section padding matches the originals
          (p-7 sm:p-12). Each section supplies its own inner max-width. */}
      <div className="absolute inset-0 flex items-end p-7 sm:p-12">{children}</div>
    </motion.section>
  );
}
